import { memo } from 'react'

import { withSearch } from '@elastic/react-search-ui'
import type { SearchContextState } from '@elastic/react-search-ui/lib/cjs/withSearch'
import { FormProvider } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'

import Button from 'app/components/base/Button'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import SelectField from 'app/components/fields/SelectField'
import TextField from 'app/components/fields/TextField'
import { transformGenerationOptions } from 'app/components/forms/template/GeneralListingFormTemplate/helpers/carInfo.helpers'
import { getCategories, getMakes, getModels } from 'app/services/Api/listing'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { MAKE_SOURCE_TYPE } from 'app/utils/constants/listing.constants'
import { getSubcategoryHeader } from 'app/utils/helpers/listings.helpers'

import { getLetsMatchedModelId } from '../../helpers/match-me.helpers'
import { useLetsMatchedForm } from '../../hooks/useLetsMatchedForm'

import SendMatchNotificationDialog from './SendMatchNotificationDialog'

const selector = ({ filters }: SearchContextState) => ({
  filters
})

const widthStyle = { width: { xs: '100%', sm: 350 } }

const LetsMatchedForm = ({ filters }: SearchContextState) => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()

  const {
    methods,
    make,
    model,
    isGenerationDisabled,
    categoryId,
    subCategoryOptions,
    handleSubmit,
    isSubmitting,
    open,
    hideModal,
    isActiveButton
  } = useLetsMatchedForm(filters)

  return (
    <>
      <FormProvider {...methods}>
        <Stack
          direction="column"
          spacing={2}
          sx={{ alignItems: 'center', maxWidth: { sm: 720 } }}
        >
          <Text variant="h2">
            {formatMessage({ defaultMessage: "Let's get you matched" })}
          </Text>

          <Text
            variant="body1"
            sx={{ color: 'text.third', textAlign: 'center' }}
          >
            {formatMessage(
              {
                defaultMessage:
                  'Fill in Make, Model, Category and Subcategory so we can alert you when your part is listed. {br} {proTip} Add a Part Number or Generation for the best results.'
              },
              {
                br: IS_NATIVE_APP ? '\n' : <br />,
                proTip: (
                  <Text
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      ...(!IS_NATIVE_APP && { display: 'inline' })
                    }}
                  >
                    {formatMessage({ defaultMessage: 'Pro tip:' })}
                  </Text>
                )
              }
            )}
          </Text>

          <Stack
            direction="column"
            spacing={1}
            sx={{ width: '100%', alignItems: 'center' }}
          >
            <SelectField
              name="makes"
              withFilter
              optionSettings={{
                key: 'makes',
                loadOptions: () => getMakes([MAKE_SOURCE_TYPE.imported]),
                optionValue: 'name',
                optionLabel: 'name'
              }}
              label={formatMessage({ defaultMessage: 'Make' })}
              formControlProps={{ sx: widthStyle }}
            />

            <SelectField
              name="models"
              disabled={!make}
              withFilter
              sortOption
              label={formatMessage({ defaultMessage: 'Model' })}
              optionSettings={{
                key: make,
                enabled: !!make,
                loadOptions: async () => {
                  if (!make) return []

                  const res = await getModels({ by_make_name: [make] })

                  return res
                },
                optionValue: 'name',
                optionLabel: 'name'
              }}
              formControlProps={{ sx: widthStyle }}
            />

            <SelectField
              name="category_id"
              withFilter
              sortOption
              optionSettings={{ key: 'category', loadOptions: getCategories }}
              label={formatMessage({ defaultMessage: 'Category' })}
              formControlProps={{ sx: widthStyle }}
            />

            <SelectField
              name="sub_category_id"
              withFilter
              disabled={!categoryId}
              sortOption
              groupBy={getSubcategoryHeader}
              optionSettings={{ options: subCategoryOptions }}
              label={formatMessage({ defaultMessage: 'Sub-Category' })}
              formControlProps={{ sx: widthStyle }}
            />

            <TextField
              name="part_number"
              label={formatMessage({ defaultMessage: 'Part Number' })}
              formControlProps={{ sx: widthStyle }}
              helperText={formatMessage({ defaultMessage: 'Optional field' })}
            />

            <SelectField
              name="generation_id"
              withFilter
              disabled={isGenerationDisabled}
              label={formatMessage({ defaultMessage: 'Generation' })}
              helperText={formatMessage({ defaultMessage: 'Optional field' })}
              optionSettings={{
                key: model,
                enabled: !!model,
                loadOptions: async () => {
                  const modelId = await getLetsMatchedModelId({
                    queryClient,
                    make,
                    model
                  })

                  if (!modelId) return []

                  const resp = transformGenerationOptions([modelId])

                  return resp
                },
                optionValue: 'id',
                optionLabel: 'name'
              }}
              formControlProps={{ sx: widthStyle }}
            />
          </Stack>

          <Button
            variant="contained"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!isActiveButton}
            sx={{ ...widthStyle }}
          >
            {formatMessage({ defaultMessage: 'Notify Me When Available' })}
          </Button>
        </Stack>
      </FormProvider>

      {open && (
        <SendMatchNotificationDialog
          open={open}
          onSend={handleSubmit}
          onClose={hideModal}
          isSubmitting={isSubmitting}
          methods={methods}
        />
      )}
    </>
  )
}

export default memo(withSearch(selector)(LetsMatchedForm))
